import * as React from 'react';
import { TextInput } from 'components/Input';
import { FunctionComponent, useState } from 'react';
import { InputFormProps } from 'components/Sov/components/SovEditGrid/components/DataGrid/components/DataGrid';
import EditNote from '@mui/icons-material/EditNote';
import { Divider, IconButton, InputAdornment, Popover, Typography } from '@mui/material';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import LrButton from '../../../../../../components/LrButton/components/Button';
import { Grid } from '@mui/material';
import { currencyMask } from 'helpers/masks';
import { Notes } from '@mui/icons-material';
import { unformatNumberString } from 'helpers/formatters';
import { useSelector } from 'react-redux';
import { getLocationSoftCostTypesList } from '../../../../../redux/selectors';

interface SoftCostLineItem {
    id?: number | null;
    label: string | null;
    value: number;
    typeId?: number | null;
}

interface Props extends InputFormProps {
    value: Array<SoftCostLineItem>;
    onChange: (value: Array<SoftCostLineItem>) => void;
    parentData: { softCosts: number };
    isReadOnly?: boolean;
}

const SoftCostLineItemsInputForm: FunctionComponent<Props> = (props) => {
    const {
        value = [],
        onChange,
        parentData: { softCosts: manualSoftCostsTotal } = { softCosts: 0 },
        isReadOnly = false,
    } = props;

    // Get locationSoftCostTypes from global state
    const locationSoftCostTypes = useSelector(getLocationSoftCostTypesList);

    // Helper: get required line items from value or create them if missing
    const getRequiredLineItems = () => {
        return locationSoftCostTypes.map((locationSoftCostType) => {
            const existing = value.find((item) => item.typeId === locationSoftCostType.id);
            return (
                existing || {
                    id: null,
                    label: locationSoftCostType.displayValue,
                    value: 0,
                    typeId: locationSoftCostType.id,
                }
            );
        });
    };

    // Helper: get optional line items (not required)
    const getOptionalLineItems = () => value.filter((item) => !item.typeId);

    const initialFormData = [...getRequiredLineItems(), ...getOptionalLineItems()];
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [formData, setFormData] = useState(initialFormData);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const getTotalOfLineItems = (): number => {
        return formData.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
    };

    const handleClickEditIcon = (event?: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event?.currentTarget || null);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const handleChange = (updatedData: Array<SoftCostLineItem>) => {
        setFormData(updatedData);
        const hasEmptyLabel = updatedData.some(
            (item) => !(item.label?.trim() && item.value !== null && item.value >= 0)
        );
        setHasError(hasEmptyLabel);
        setErrorMessage(hasEmptyLabel ? 'must enter labels and values for all line items' : '');
    };

    const handleSave = () => {
        onChange(formData);
        handleClosePopover();
    };

    const handleCancel = () => {
        setFormData(props.value);
        handleClosePopover();
        setHasError(false);
    };

    const showForm = Boolean(anchorEl);

    return (
        <>
            {isReadOnly ? (
                <Grid container alignItems="center" spacing={0}>
                    <Grid item>
                        <Typography variant="body1" color="text.primary" style={{ marginBottom: '0' }}>
                            {value
                                .reduce((sum, item) => sum + (Number(item.value) || 0), 0)
                                .toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                    maximumFractionDigits: 0,
                                })}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <IconButton
                            aria-label="Soft cost line items"
                            onClick={handleClickEditIcon}
                            style={{ paddingBottom: '12px' }}
                        >
                            <Notes fontSize="small" />
                        </IconButton>
                    </Grid>
                </Grid>
            ) : (
                <TextInput
                    value={value
                        .reduce((sum, item) => sum + (Number(item.value) || 0), 0)
                        .toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            maximumFractionDigits: 0,
                        })}
                    variant="underlined"
                    spacing={0}
                    readOnly={true}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton aria-label="Soft cost line items" onClick={handleClickEditIcon}>
                                <EditNote fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    }
                ></TextInput>
            )}
            <Popover
                open={showForm}
                onClose={handleClosePopover}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
            >
                <div style={{ padding: 24, minWidth: 600 }}>
                    {!formData.length && (
                        <Typography variant="h3" style={{ margin: 0, marginBottom: 16 }}>
                            No Line Items
                        </Typography>
                    )}
                    <Grid container direction="column" spacing={2}>
                        {formData.length > 0 && (
                            // Column headers
                            <Grid item>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={6}>
                                        <Typography variant="h5">Soft Cost Line Item</Typography>
                                        <Divider variant="fullWidth"></Divider>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="h5">Value</Typography>
                                        <Divider variant="fullWidth"></Divider>
                                    </Grid>
                                    <Grid item xs={2}>
                                        {!isReadOnly && (
                                            <>
                                                <Typography variant="h5">Remove</Typography>
                                                <Divider variant="fullWidth"></Divider>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                        {(formData.length > 0 ? formData : []).map((item, idx) => {
                            const isRequired = locationSoftCostTypes.some((type) => type.displayValue === item.label);
                            return (
                                <Grid item key={idx}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={6}>
                                            {isRequired || isReadOnly ? (
                                                <Typography variant="body1" style={{ whiteSpace: 'nowrap' }}>
                                                    {item.label.trim()}
                                                </Typography>
                                            ) : (
                                                <TextInput
                                                    variant="underlined"
                                                    error={!item.label}
                                                    value={item.label}
                                                    onChange={(newValue) => {
                                                        const updated = formData.map((v, i) =>
                                                            i === idx ? { ...v, label: newValue.target.value || '' } : v
                                                        );
                                                        handleChange(updated);
                                                    }}
                                                    placeholder="Label"
                                                />
                                            )}
                                        </Grid>
                                        <Grid item xs={4}>
                                            {isReadOnly ? (
                                                <Typography variant="body1">
                                                    {Number(item.value).toLocaleString('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                        maximumFractionDigits: 0,
                                                    })}
                                                </Typography>
                                            ) : (
                                                <TextInput
                                                    variant="underlined"
                                                    style={{ minWidth: 120 }}
                                                    placeholder="$1000"
                                                    value={item.value}
                                                    mask={currencyMask}
                                                    onChange={(e) => {
                                                        const numericValue = Number(
                                                            (e.target.value || '').replace(/[^\d.-]/g, '')
                                                        );
                                                        const updated = formData.map((v, i) =>
                                                            i === idx ? { ...v, value: numericValue } : v
                                                        );
                                                        handleChange(updated);
                                                    }}
                                                />
                                            )}
                                        </Grid>
                                        <Grid item xs={2}>
                                            {!isReadOnly && !isRequired && (
                                                <IconButton
                                                    size="small"
                                                    aria-label="Remove"
                                                    onClick={() => {
                                                        const updated = formData.filter((_, i) => i !== idx);
                                                        handleChange(updated);
                                                    }}
                                                >
                                                    <RemoveCircleOutline color="error" fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            );
                        })}
                        {hasError && !isReadOnly && (
                            <Grid item>
                                <Typography variant="body2" color="error">
                                    {errorMessage}
                                </Typography>
                            </Grid>
                        )}
                        {/* Total row */}
                        {formData.length > 0 && (
                            <Grid item marginTop={2}>
                                <Grid container spacing={2} alignItems="center" marginTop={1}>
                                    <Grid item xs={6} marginLeft={0}>
                                        <Divider variant="fullWidth"></Divider>
                                        <Typography variant="h4" fontWeight="bold" marginTop={1}>
                                            Total:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4} marginLeft={0}>
                                        <Divider variant="fullWidth"></Divider>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                            marginTop={1}
                                            color={
                                                !!manualSoftCostsTotal &&
                                                getTotalOfLineItems() !== unformatNumberString(manualSoftCostsTotal)
                                                    ? 'error.main'
                                                    : 'text.primary'
                                            }
                                        >
                                            {formData
                                                .reduce((sum, item) => sum + (Number(item.value) || 0), 0)
                                                .toLocaleString('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                    maximumFractionDigits: 0,
                                                })}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={2}></Grid>
                                </Grid>
                            </Grid>
                        )}
                        {!isReadOnly && (
                            <Grid item>
                                <Grid container justifyContent="space-between" alignItems="center" marginTop={4}>
                                    <Grid item>
                                        <Grid container spacing={1}>
                                            <Grid item>
                                                <LrButton lrType="secondary" onClick={handleCancel}>
                                                    Cancel
                                                </LrButton>
                                            </Grid>
                                            <Grid item>
                                                <LrButton lrType="primary" onClick={handleSave} disabled={hasError}>
                                                    OK
                                                </LrButton>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item>
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item></Grid>
                                            <Grid item>
                                                <LrButton
                                                    lrType="text"
                                                    onClick={() => {
                                                        const updated = [
                                                            ...formData,
                                                            { id: null, label: '', value: 0 },
                                                        ];
                                                        handleChange(updated);
                                                    }}
                                                >
                                                    + Add Line Item
                                                </LrButton>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </div>
            </Popover>
        </>
    );
};

export default React.memo(SoftCostLineItemsInputForm);
